// Times (docs/01 §6): chip "Time N", marcador e placar de times
// (pontos = média dos jogadores — TeamScore {team, score, members}).
import { fmt, s } from "@/shared/strings";
import { PLAYER_COLORS } from "@/shared/components/AvatarView";
import type { TeamScore } from "@/shared/protocol";

export function teamColor(team: number): string {
  return PLAYER_COLORS[(team - 1) % PLAYER_COLORS.length];
}

export function teamName(team: number): string {
  return fmt(s.team, { n: team });
}

// TeamBadge — chip colorido "Time N".
export function TeamBadge({ team, className = "" }: { team: number; className?: string }) {
  if (team < 1) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-bold text-white ${className}`}
      style={{ background: teamColor(team) }}
    >
      {teamName(team)}
    </span>
  );
}

// TeamDot — marcador de time para listas de jogadores.
export function TeamDot({ team }: { team: number }) {
  if (team < 1) return null;
  return (
    <span
      aria-hidden
      className="inline-block h-3 w-3 shrink-0 rounded-full"
      style={{ background: teamColor(team) }}
      title={teamName(team)}
    />
  );
}

// TeamStandings — placar de times ordenado por média de pontos.
// `large` usa medidas em vmin (palco a 3–8 m; mínimo 28px — docs/05 §3).
export function TeamStandings({ teams, large = false }: { teams: TeamScore[]; large?: boolean }) {
  const sorted = [...teams].sort((a, b) => b.score - a.score);
  const max = Math.max(1, ...sorted.map((t) => t.score));
  const fs = large ? "clamp(28px,3vmin,44px)" : undefined;
  return (
    <div className={large ? "flex w-[46%] flex-col gap-[1.2vmin]" : "flex flex-col gap-2"}>
      <p
        className={`text-chalk-3 text-center font-bold tracking-wide uppercase ${large ? "" : "text-14"}`}
        style={large ? { fontSize: "clamp(28px,2.4vmin,34px)" } : undefined}
      >
        {s.teamStandings}
      </p>
      {sorted.map((t, i) => (
        <div
          key={t.team}
          className={
            large
              ? "bg-bg-2 flex items-center gap-[1.5vmin] rounded-[1vmin] px-[2vmin] py-[1vmin]"
              : "bg-bg-2 flex items-center gap-3 rounded-lg px-3 py-2"
          }
        >
          <span className="expanded font-black" style={{ color: teamColor(t.team), fontSize: fs }}>
            {i + 1}º
          </span>
          <span className="truncate font-bold" style={{ fontSize: fs }}>
            {teamName(t.team)}
          </span>
          <div
            className={`bg-bg-3 flex-1 overflow-hidden rounded-full ${large ? "h-[2vmin]" : "h-2"}`}
          >
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{ width: `${(t.score / max) * 100}%`, background: teamColor(t.team) }}
            />
          </div>
          <span className="font-black tabular-nums" style={{ fontSize: fs }}>
            {t.score}
          </span>
        </div>
      ))}
    </div>
  );
}
